/**
 * Pure comparison of the editor's legacy lifecycle against the Remix state machine.
 *
 * Both sides are expressed in the shared editor's own `EditorRemixLifecyclePhase` vocabulary, so
 * diagnostics introduce no second normalization of editor state. Nothing here reads or mutates
 * runtime state: callers pass two phases plus whether the pair has settled, and get back a
 * classification. Timing and scoping are the caller's concern.
 *
 * Both projections into that vocabulary live in the shared editor package, next to each other; this
 * module owns only the classification of a pair.
 *
 * The renderer equivalent lives in `rendererRemixParity.ts`. The two are deliberately separate
 * rather than generalized: the phase vocabularies differ (the renderer projects the machine's
 * `inserting` as `submitting`; the editor does not) and each surface classifies its own skew.
 */
import type { EditorRemixLifecyclePhase } from '@atlassian/malleable-ui-components/types';
import { isTerminalEditorRemixPhase } from '@atlassian/malleable-ui-components/editor-remix/lifecycle';

/** Which producer opened the flow. Lets create and update divergence be filtered independently. */
export type EditorRemixProducerKind = 'create' | 'update';

/**
 * Where in the editor's own flow a comparison was taken.
 *
 * Anchored to the legacy phase, which is derived purely from the editor's own signals and so is the
 * side independent of the machine. Deriving it from the machine phase instead would make it a
 * relabeling of `machinePhase` and add no grouping dimension to the monitoring query.
 */
export type EditorRemixComparisonPoint =
	| 'flow-started'
	| 'result-ready'
	| 'preview-opened'
	| 'inserting'
	| 'error'
	| 'aborted'
	| 'released';

export type EditorRemixDivergenceReason =
	| 'legacy-not-tracking'
	| 'terminal-outcome-mismatch'
	| 'unexpected-phase-pair';

export type EditorRemixPendingReason = 'machine-not-tracking' | 'awaiting-stable-phases';

export type EditorRemixDivergence = Readonly<{
	producerKind: EditorRemixProducerKind;
	legacyPhase: EditorRemixLifecyclePhase;
	machinePhase: EditorRemixLifecyclePhase;
	comparisonPoint: EditorRemixComparisonPoint;
	divergenceReason: EditorRemixDivergenceReason;
}>;

export type EditorRemixParityResult =
	| Readonly<{ outcome: 'match' }>
	| Readonly<{ outcome: 'pending'; reason: EditorRemixPendingReason }>
	| Readonly<{ outcome: 'divergence'; divergence: EditorRemixDivergence }>;

const COMPARISON_POINT_BY_LEGACY_PHASE = {
	inactive: 'released',
	thinking: 'flow-started',
	ready: 'result-ready',
	previewing: 'preview-opened',
	inserting: 'inserting',
	error: 'error',
	aborted: 'aborted',
} as const satisfies Record<EditorRemixLifecyclePhase, EditorRemixComparisonPoint>;

export const comparisonPointForLegacyPhase = (
	legacyPhase: EditorRemixLifecyclePhase,
): EditorRemixComparisonPoint => COMPARISON_POINT_BY_LEGACY_PHASE[legacyPhase];

export const classifyDivergenceReason = (
	legacyPhase: EditorRemixLifecyclePhase,
	machinePhase: EditorRemixLifecyclePhase,
): EditorRemixDivergenceReason => {
	// The machine is running a flow the editor no longer considers active.
	if (legacyPhase === 'inactive') {
		return 'legacy-not-tracking';
	}

	if (isTerminalEditorRemixPhase(legacyPhase) || isTerminalEditorRemixPhase(machinePhase)) {
		return 'terminal-outcome-mismatch';
	}

	return 'unexpected-phase-pair';
};

/**
 * Classifies one legacy/machine phase pair.
 *
 * `isStable` says whether the pair has held long enough to be treated as settled. A differing pair
 * that has not settled is `pending` rather than a divergence, which is what keeps ordinary
 * asynchronous transition skew out of the signal.
 */
export const compareEditorRemixParity = ({
	producerKind,
	legacyPhase,
	machinePhase,
	isStable,
}: Readonly<{
	producerKind: EditorRemixProducerKind;
	legacyPhase: EditorRemixLifecyclePhase;
	machinePhase: EditorRemixLifecyclePhase;
	isStable: boolean;
}>): EditorRemixParityResult => {
	// `inactive` covers both admission skew and a released machine flow, and the editor releases on
	// a successful insertion while its own state settles afterwards. Comparing here would report
	// normal teardown as a divergence, so terminal pairs must be captured before release. The
	// lifecycle owner instruments its own release paths separately.
	if (machinePhase === 'inactive') {
		return { outcome: 'pending', reason: 'machine-not-tracking' };
	}

	if (legacyPhase === machinePhase) {
		return { outcome: 'match' };
	}

	if (!isStable) {
		return { outcome: 'pending', reason: 'awaiting-stable-phases' };
	}

	return {
		outcome: 'divergence',
		divergence: {
			producerKind,
			legacyPhase,
			machinePhase,
			comparisonPoint: comparisonPointForLegacyPhase(legacyPhase),
			divergenceReason: classifyDivergenceReason(legacyPhase, machinePhase),
		},
	};
};
